// ─── Navigation ──────────────────────────────────────────────────────────────
// Runs after components/navbar.html is injected by the component loader.

document.addEventListener('components:ready', function (): void {
    const navbar = document.getElementById('navbar');
    const navToggle = document.getElementById('navToggle') as HTMLButtonElement | null;
    const navMenu = document.getElementById('navMenu') as HTMLUListElement | null;

    if (!navbar || !navToggle || !navMenu) return;

    // Scroll effect
    let lastScroll = 0;
    window.addEventListener('scroll', () => {
        const currentScroll = window.pageYOffset;
        navbar.classList.toggle('scrolled', currentScroll > 50);
        lastScroll = currentScroll;
    });

    // Mobile menu toggle
    navToggle.addEventListener('click', () => {
        navMenu.classList.toggle('active');
        navToggle.classList.toggle('active');
    });

    // Close menu when a nav link is clicked
    document.querySelectorAll<HTMLAnchorElement>('.nav-link').forEach(link => {
        link.addEventListener('click', () => {
            navMenu.classList.remove('active');
            navToggle.classList.remove('active');
        });
    });

    // Smooth scroll for anchor links
    document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e: MouseEvent): void {
            e.preventDefault();
            const href = this.getAttribute('href');
            if (!href) return;
            const target = document.querySelector<HTMLElement>(href);
            if (target) {
                window.scrollTo({ top: target.offsetTop - 80, behavior: 'smooth' });
            }
        });
    });
});

// ─── Intersection Observer — scroll reveal ───────────────────────────────────

const observerOptions: IntersectionObserverInit = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
};

const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            entry.target.classList.add('active');
        }
    });
}, observerOptions);

document.addEventListener('DOMContentLoaded', () => {
    document
        .querySelectorAll<HTMLElement>('.problem-card, .feature-card, .capability-item, .mission-card, .audience-card')
        .forEach(el => {
            el.classList.add('reveal');
            revealObserver.observe(el);
        });
});

// ─── Features section: satellite bg fade in / out ────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    const featuresSection = document.querySelector<HTMLElement>('.features-section');
    if (featuresSection) {
        new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    featuresSection.classList.toggle('bg-visible', entry.isIntersecting);
                });
            },
            { threshold: 0.15 }
        ).observe(featuresSection);
    }
});

// ─── Section reveal animations ───────────────────────────────────────────────

const sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
        }
    });
}, { threshold: 0.1 });

document.querySelectorAll<HTMLElement>('section').forEach(section => {
    section.style.opacity = '0';
    section.style.transform = 'translateY(30px)';
    section.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
    sectionObserver.observe(section);
});

// ─── Parallax — hero background logo ─────────────────────────────────────────

function throttle<T extends (...args: Parameters<T>) => void>(func: T, wait: number): T {
    let timeout: number | undefined;
    return function executedFunction(this: unknown, ...args: Parameters<T>): void {
        const later = (): void => {
            window.clearTimeout(timeout);
            func.apply(this, args);
        };
        window.clearTimeout(timeout);
        timeout = window.setTimeout(later, wait);
    } as T;
}

const handleScroll = throttle((): void => {
    const scrolled = window.pageYOffset;
    const heroLogo = document.querySelector<HTMLElement>('.hero-background-logo');
    if (heroLogo && scrolled < window.innerHeight) {
        heroLogo.style.transform = `translateY(${scrolled * 0.3}px) scale(${1 + scrolled * 0.0005})`;
        heroLogo.style.opacity = String(Math.max(0.08 - scrolled / 1000, 0.03));
    }
}, 10);

window.addEventListener('scroll', handleScroll);

// ─── Contact form ─────────────────────────────────────────────────────────────

const contactForm    = document.getElementById('contactForm')    as HTMLFormElement | null;
const contactSuccess = document.getElementById('contactSuccess') as HTMLElement | null;
const contactAgain   = document.getElementById('contactAgain')   as HTMLButtonElement | null;

if (contactForm && contactSuccess) {

    const emailInput = document.getElementById('cf-email')   as HTMLInputElement  | null;
    const msgInput   = document.getElementById('cf-message') as HTMLTextAreaElement | null;
    const emailError = document.getElementById('cf-email-error');
    const msgError   = document.getElementById('cf-msg-error');

    function setFieldError(input: HTMLElement | null, errorEl: HTMLElement | null, msg: string): void {
        input?.classList.toggle('field-invalid', msg !== '');
        if (errorEl) errorEl.textContent = msg;
    }

    function validateContactForm(): boolean {
        let ok = true;

        const emailVal = emailInput?.value.trim() ?? '';
        const emailOk  = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailVal);
        if (!emailOk) {
            setFieldError(emailInput, emailError, 'Please enter a valid email address.');
            ok = false;
        } else {
            setFieldError(emailInput, emailError, '');
        }

        const msgVal = msgInput?.value.trim() ?? '';
        if (msgVal.length < 20) {
            setFieldError(msgInput, msgError, `Message too short — ${msgVal.length}/20 characters minimum.`);
            if (ok) msgInput?.focus();
            ok = false;
        } else {
            setFieldError(msgInput, msgError, '');
        }

        return ok;
    }

    // Clear errors live as user types
    emailInput?.addEventListener('input', () => setFieldError(emailInput, emailError, ''));
    msgInput?.addEventListener('input',   () => setFieldError(msgInput,   msgError,   ''));

    contactForm.addEventListener('submit', async (e: Event): Promise<void> => {
        e.preventDefault();

        if (!validateContactForm()) return;

        const data = Object.fromEntries(new FormData(contactForm) as unknown as Iterable<[string, FormDataEntryValue]>);
        const submitBtn = contactForm.querySelector<HTMLButtonElement>('button[type="submit"]');

        if (submitBtn) submitBtn.disabled = true;

        try {
            const res = await fetch('/api/contact', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify(data),
            });

            if (res.ok) {
                contactForm.reset();
                contactForm.hidden = true;
                contactSuccess.hidden = false;
            } else {
                const { error } = await res.json() as { error: string };
                alert(error || 'Something went wrong. Please try again.');
                if (submitBtn) submitBtn.disabled = false;
            }
        } catch {
            alert('Network error. Please check your connection and try again.');
            if (submitBtn) submitBtn.disabled = false;
        }
    });

    function resetToForm(): void {
        contactSuccess!.hidden = true;
        contactForm!.hidden = false;
        const submitBtn = contactForm!.querySelector<HTMLButtonElement>('button[type="submit"]');
        if (submitBtn) submitBtn.disabled = false;
    }

    contactAgain?.addEventListener('click', resetToForm);

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (!contactSuccess.hidden && e.key.length === 1) {
            resetToForm();
            document.getElementById('cf-name')?.focus();
        }
    });
}

// ─── Button hover effect ──────────────────────────────────────────────────────

document.querySelectorAll<HTMLElement>('.btn').forEach(btn => {
    btn.addEventListener('mouseenter', function (this: HTMLElement): void {
        this.style.transform = 'translateY(-2px)';
    });
    btn.addEventListener('mouseleave', function (this: HTMLElement): void {
        this.style.transform = 'translateY(0)';
    });
});

// ─── Page load animations ─────────────────────────────────────────────────────

window.addEventListener('load', (): void => {
    document.body.style.opacity = '0';
    setTimeout(() => {
        document.body.style.transition = 'opacity 0.5s ease-in';
        document.body.style.opacity = '1';
    }, 100);

    const firstSection = document.querySelector<HTMLElement>('.hero');
    if (firstSection) firstSection.classList.add('active');

    document.body.classList.add('loaded');
});

// ─── Countdown Timer ──────────────────────────────────────────────────────────

function initCountdown(): void {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 3);

    const daysEl = document.getElementById('days');
    const hoursEl = document.getElementById('hours');
    const minutesEl = document.getElementById('minutes');
    const secondsEl = document.getElementById('seconds');

    if (!daysEl || !hoursEl || !minutesEl || !secondsEl) return;

    const updateWithAnimation = (el: HTMLElement, value: number): void => {
        const padded = String(value).padStart(2, '0');
        if (el.textContent !== padded) {
            el.classList.add('updated');
            el.textContent = padded;
            setTimeout(() => el.classList.remove('updated'), 500);
        } else {
            el.textContent = padded;
        }
    };

    function updateCountdown(): void {
        const distance = targetDate.getTime() - Date.now();

        if (distance < 0) {
            [daysEl!, hoursEl!, minutesEl!, secondsEl!].forEach(el => (el.textContent = '00'));
            return;
        }

        updateWithAnimation(daysEl!, Math.floor(distance / (1000 * 60 * 60 * 24)));
        updateWithAnimation(hoursEl!, Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
        updateWithAnimation(minutesEl!, Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
        updateWithAnimation(secondsEl!, Math.floor((distance % (1000 * 60)) / 1000));
    }

    updateCountdown();
    setInterval(updateCountdown, 1000);
}

document.addEventListener('DOMContentLoaded', () => {
    initCountdown();
});

// ─── Utility: animate a numeric value ────────────────────────────────────────
// Exported for use in future components (e.g. statistics sections).

function animateValue(element: HTMLElement, start: number, end: number, duration: number): void {
    let startTimestamp: number | null = null;
    const step = (timestamp: number): void => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        element.textContent = Math.floor(progress * (end - start) + start).toString();
        if (progress < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
}

// ─── Utility: typewriter effect ──────────────────────────────────────────────

function typeWriter(element: HTMLElement, text: string, speed = 100): void {
    let i = 0;
    element.textContent = '';
    function type(): void {
        if (i < text.length) {
            element.textContent += text.charAt(i++);
            setTimeout(type, speed);
        }
    }
    type();
}

// Expose utilities on window for use from other scripts if needed
(window as Window & typeof globalThis & {
    animateValue: typeof animateValue;
    typeWriter: typeof typeWriter;
}).animateValue = animateValue;

(window as Window & typeof globalThis & {
    typeWriter: typeof typeWriter;
}).typeWriter = typeWriter;
